// Refresh missing history when a background/offline chart becomes usable again.
export function watchChartRecovery({ win, doc, recover, acceptsPacket = () => false, now = Date.now, staleMs = 20000, delay = 250 }) {
  let timer, running = false, dirty = false, disposed = false, failures = 0;
  let lastPacketAt = now(), connected = true;
  let away = doc.visibilityState === "hidden", incompleteFeed = false;
  const usable = () => doc.visibilityState !== 'hidden' && win.navigator.onLine !== false;
  const schedule = () => {
    if (disposed || running || timer || !dirty || !usable() || !connected) return;
    timer = win.setTimeout(run, delay * Math.pow(2, failures));
  };
  const run = async () => {
    timer = undefined;
    if (disposed || !usable() || !connected) return;
    dirty = false;
    running = true;
    let ok = false;
    try { ok = await recover(); } catch { /* Retry transient fetch failures. */ }
    running = false;
    if (disposed) return;
    if (ok) { failures = 0; lastPacketAt = now(); }
    else if (++failures < 4) dirty = true;
    schedule();
  };
  const mark = () => { dirty = true; failures = 0; schedule(); };
  const left = () => { away = true; };
  const changed = () => {
    if (doc.visibilityState === 'hidden') { left(); return; }
    if (!usable()) return;
    // Fresh quote snapshots do not guarantee complete OHLC while backgrounded.
    // Reconcile those candles with the same history used by a full refresh.
    const needsHistory = away && incompleteFeed;
    away = false;
    if (needsHistory || (!running && now() - lastPacketAt >= staleMs)) mark();
    else schedule();
  };
  const closed = () => { connected = false; dirty = true; };
  const opened = () => { connected = true; schedule(); };
  const packet = event => {
    if (!acceptsPacket(event.detail)) return;
    incompleteFeed = event.detail?.ohlcComplete === false;
    const missed = now() - lastPacketAt >= staleMs;
    lastPacketAt = now();
    if (missed && !running) mark();
  };
  doc.addEventListener('visibilitychange', changed);
  doc.addEventListener('connectionClosed', closed);
  doc.addEventListener('connectionOpened', opened);
  doc.addEventListener('ws_message_platform', packet);
  win.addEventListener('online', changed);
  win.addEventListener('offline', mark);
  win.addEventListener('pageshow', changed);
  win.addEventListener('blur', left);
  win.addEventListener('focus', changed);
  return () => {
    disposed = true;
    win.clearTimeout(timer);
    doc.removeEventListener('visibilitychange', changed);
    doc.removeEventListener('connectionClosed', closed);
    doc.removeEventListener('connectionOpened', opened);
    doc.removeEventListener('ws_message_platform', packet);
    for (const event of ['online', 'pageshow']) win.removeEventListener(event, changed);
    win.removeEventListener('offline', mark);
    win.removeEventListener('blur', left);
    win.removeEventListener('focus', changed);
  };
}
